import React, { useState } from 'react';
import {
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { DateRange } from '@/features/dashboard/reports/models';
import { isBrowser } from '@/utils/browser';
import { CustomDateParam } from '@/utils/date-params';

const LOCAL_STORAGE_DATE_RANGE_KEY = `vsly_reporting_date_range`;
const LOCAL_STORAGE_START_DATE_KEY = `vsly_reporting_start_date`;
const LOCAL_STORAGE_END_DATE_KEY = `vsly_reporting_end_date`;
export const DateRangeParam = withDefault(
  StringParam,
  defaultDateRange(),
) as QueryParamConfig<string, DateRange>;
export const StartDateParameter = withDefault(
  CustomDateParam,
  defaultStartDate(),
);
export const EndDateParameter = withDefault(CustomDateParam, defaultEndDate());

const datePickerQueryConfig = {
  rn: DateRangeParam,
  s: StartDateParameter,
  e: EndDateParameter,
};

export function newDatePickerCtx(): DatePickerContext {
  const [query, setQuery] = useQueryParams(datePickerQueryConfig);

  const dateRange = query.rn as DateRange;
  const startDate = query.s as Date;
  const endDate = query.e as Date;

  const [ranges, setRanges] = useState([
    {
      startDate,
      endDate,
      key: `rollup`,
    },
  ]);

  const persistDateRange = (x: DateRange) => {
    setQuery({ rn: x }, `replaceIn`);
    localStorage?.setItem(LOCAL_STORAGE_DATE_RANGE_KEY, x);
  };

  const setCustomRange = (start: Date, end: Date) => {
    setQuery({ s: start, e: end, rn: DateRange.CUSTOM }, `replaceIn`);
    localStorage?.setItem(LOCAL_STORAGE_START_DATE_KEY, start.toISOString());
    localStorage?.setItem(LOCAL_STORAGE_END_DATE_KEY, end.toISOString());
    localStorage?.setItem(LOCAL_STORAGE_DATE_RANGE_KEY, DateRange.CUSTOM);
  };

  return {
    dateRange,
    setDateRange: persistDateRange,
    startDate,
    endDate,
    setCustomRange,
    ranges,
    setRanges,
  };
}

function defaultDateRange(): DateRange {
  if (!isBrowser()) {
    return DateRange.LAST_30_DAYS;
  }
  return (window?.localStorage?.getItem(LOCAL_STORAGE_DATE_RANGE_KEY) ||
    DateRange.LAST_30_DAYS) as DateRange;
}

function defaultStartDate() {
  if (!isBrowser()) {
    return new Date();
  }
  const lsStartDate = window?.localStorage?.getItem(
    LOCAL_STORAGE_START_DATE_KEY,
  );
  return lsStartDate ? new Date(lsStartDate) : new Date();
}

function defaultEndDate() {
  if (!isBrowser()) {
    return new Date();
  }
  const lsStartDate = window?.localStorage?.getItem(LOCAL_STORAGE_END_DATE_KEY);
  return lsStartDate ? new Date(lsStartDate) : new Date();
}

export interface DatePickerContext {
  dateRange: DateRange;
  setDateRange: (x: DateRange) => any;
  startDate: Date;
  endDate: Date;
  setCustomRange: (start: Date, end: Date) => any;
  ranges: { startDate: Date; endDate: Date; key: string }[];
  setRanges: React.Dispatch<
    React.SetStateAction<{ startDate: Date; endDate: Date; key: string }[]>
  >;
}
export const DataPickerContext = React.createContext({} as DatePickerContext);
